import React, { useEffect, useState, useCallback } from 'react';
import { getAlerts, createAlert, updateAlert, deleteAlert, testAlert, evaluateAlerts, getClients } from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, Pencil, Bell, BellRing, Send, Play } from 'lucide-react';
import { toast } from 'sonner';
import { AlertRuleFormDialog, ALERT_TYPES } from '../components/alerts/AlertRuleFormDialog';

const emptyForm = { name: '', alert_type: '', conditions: {}, recipients: '', send_to_self: true, enabled: true };

export default function AlertsPage() {
  const [rules, setRules] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [alertRes, clientRes] = await Promise.all([getAlerts(), getClients()]);
      setRules(alertRes.data);
      setClients(clientRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, alert_type: r.alert_type, conditions: r.conditions || {}, recipients: (r.recipients || []).join(', '), send_to_self: r.send_to_self !== false, enabled: r.enabled !== false });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, recipients: form.recipients.split(',').map(e => e.trim()).filter(Boolean) };
      if (editing) { await updateAlert(editing.id, payload); toast.success('Alert rule updated'); }
      else { await createAlert(payload); toast.success('Alert rule created'); }
      setDialogOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving alert rule'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try { await deleteAlert(id); toast.success('Alert deleted'); fetchData(); }
    catch { toast.error('Error deleting alert'); }
  };

  const handleTest = async (id) => {
    try { const { data } = await testAlert(id); toast.success(data.message); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error sending test'); }
  };

  const handleEvaluateAll = async () => {
    setEvaluating(true);
    try {
      const { data } = await evaluateAlerts();
      const triggered = data.results.filter(r => r.triggered).length;
      toast.success(`Evaluated ${data.evaluated} rules. ${triggered} triggered.`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error evaluating alerts'); }
    finally { setEvaluating(false); }
  };

  const handleToggle = async (rule) => {
    try { await updateAlert(rule.id, { enabled: !rule.enabled }); fetchData(); }
    catch { toast.error('Error toggling alert'); }
  };

  if (loading) return <div className="h-64 bg-zinc-100 animate-pulse" data-testid="alerts-loading" />;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Email Alerts</h1>
          <p className="text-sm text-zinc-500 mt-1">Set up custom rules to receive email notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleEvaluateAll} disabled={evaluating} variant="outline" className="rounded-none border-zinc-300 gap-2" data-testid="evaluate-all-button"><Play size={14} /> {evaluating ? 'Running...' : 'Run All Alerts'}</Button>
          <Button onClick={openCreate} className="rounded-none bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-alert-button"><Plus size={16} /> New Alert Rule</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 p-5"><div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Rules</div><div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{rules.length}</div></div>
        <div className="bg-white border border-zinc-200 p-5"><div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Active</div><div className="font-mono text-2xl font-bold text-emerald-600 mt-1">{rules.filter(r => r.enabled).length}</div></div>
        <div className="bg-white border border-zinc-200 p-5"><div className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Total Triggers</div><div className="font-mono text-2xl font-bold text-zinc-900 mt-1">{rules.reduce((s, r) => s + (r.trigger_count || 0), 0)}</div></div>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center"><Bell size={40} className="mx-auto text-zinc-300 mb-4" /><p className="text-zinc-500 text-sm">No alert rules yet.</p></div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <div key={r.id} className={`bg-white border ${r.enabled ? 'border-zinc-200' : 'border-zinc-100 opacity-60'} p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-150 hover:border-zinc-300`} data-testid={`alert-row-${r.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center text-lg shrink-0">{r.enabled ? <BellRing size={18} className="text-zinc-700" /> : <Bell size={18} className="text-zinc-400" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-zinc-900 truncate">{r.name}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs font-bold px-2 py-0.5 bg-zinc-100 text-zinc-700">{ALERT_TYPES.find(t => t.value === r.alert_type)?.label || r.alert_type}</span>
                    <span className="text-xs text-zinc-400">{(r.recipients || []).length} recipient(s)</span>
                    {r.last_triggered && <span className="text-xs text-zinc-400">Last: {r.last_triggered.split('T')[0]}</span>}
                    <span className="text-xs font-mono text-zinc-400">{r.trigger_count || 0} triggers</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={r.enabled} onCheckedChange={() => handleToggle(r)} data-testid={`toggle-alert-${r.id}`} />
                <Button variant="ghost" size="sm" onClick={() => handleTest(r.id)} className="h-8 px-2 gap-1 text-xs text-zinc-500" data-testid={`test-alert-${r.id}`}><Send size={12} /> Test</Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="h-8 w-8 p-0" data-testid={`edit-alert-${r.id}`}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-alert-${r.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertRuleFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} form={form} setForm={setForm} onSave={handleSave} saving={saving} clients={clients} />
    </div>
  );
}
